// The signed-in frame shared by both portals: a sidebar with the brand mark,
// icon nav and a dune horizon at its foot; a slim top bar on phones that
// opens the same sidebar as a drawer (one nav element in the DOM, so the
// theme switch and sign-out exist exactly once); and a <main> that re-runs
// the single route reveal on every pathname change.
//
// `tone="admin"` paints the sidebar in ink (the founder console is a
// different place from a host's desk and should feel like one); `business`
// keeps the cream surface.
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { CloseIcon, MenuIcon } from '@/icons/nav';

export interface ShellNavItem {
  to: string;
  label: string;
  icon?: ReactNode;
}

export function BrandMark({ size = 32, tone = 'business' }: { size?: number; tone?: 'business' | 'admin' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true" className="shrink-0">
      <rect width="512" height="512" rx="128" fill={tone === 'admin' ? 'var(--canyon)' : 'var(--canyon)'} />
      <path
        d="M256 104c-66 0-118 51-118 115 0 86 118 189 118 189s118-103 118-189c0-64-52-115-118-115zm0 160a45 45 0 1 1 0-90 45 45 0 0 1 0 90z"
        fill="#FAF6EF"
      />
    </svg>
  );
}

/** Layered dunes for the sidebar foot. Colours come from the tokens so the
 * admin (ink) and business (cream) sidebars each get a horizon that reads. */
function Horizon({ tone }: { tone: 'business' | 'admin' }) {
  const a = tone === 'admin' ? 'rgba(255,255,255,0.06)' : 'var(--tint-sand)';
  const b = tone === 'admin' ? 'rgba(255,255,255,0.10)' : 'color-mix(in srgb, var(--canyon) 28%, var(--surface))';
  const c = tone === 'admin' ? 'var(--canyon)' : 'var(--canyon)';
  return (
    <div className="ng-horizon" aria-hidden="true">
      <svg viewBox="0 0 248 120" preserveAspectRatio="none">
        <circle cx="196" cy="38" r="16" fill="var(--gold)" opacity={tone === 'admin' ? 0.9 : 0.85} />
        <path d="M0 78c40-26 78-30 118-12 36 16 70 14 130-12v66H0z" fill={a} />
        <path d="M0 96c46-22 86-24 126-8 34 14 66 12 122-8v40H0z" fill={b} />
        <path d="M0 112c50-16 94-16 134-4 30 9 60 8 114-6v18H0z" fill={c} opacity={tone === 'admin' ? 0.9 : 0.75} />
      </svg>
    </div>
  );
}

export function AppShell({
  tone,
  productName,
  nav,
  sidebarTop,
  sidebarBottom,
  banner,
  children,
}: {
  tone: 'business' | 'admin';
  /** "Business" / "Admin" — the word after the brand name. */
  productName: string;
  nav: ShellNavItem[];
  /** Below the brand (the property switcher). */
  sidebarTop?: ReactNode;
  /** Above the horizon (theme switch, sign-out). */
  sidebarBottom?: ReactNode;
  /** Full-width strip between the top bar and the page (sync bar, update bar). */
  banner?: ReactNode;
  children: ReactNode;
}) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the drawer on navigation and on Escape.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const admin = tone === 'admin';
  const asideClass = admin
    ? 'bg-ink-bar text-white border-r border-white/10'
    : 'bg-surface text-ink border-r border-hair';
  const navBase = admin
    ? 'text-white/70 hover:bg-white/10 hover:text-white [&.active]:bg-white/10 [&.active]:text-white'
    : 'text-ink-2 hover:bg-surface-2 hover:text-ink [&.active]:bg-tint-sand [&.active]:text-canyon-ink';

  return (
    <div className="min-h-full lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      {/* Phone top bar */}
      <header
        className={`sticky top-0 z-30 flex items-center gap-md px-lg py-sm lg:hidden ${
          admin ? 'bg-ink-bar text-white' : 'border-b border-hair bg-surface text-ink'
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-expanded={open}
          aria-controls="portal-sidebar"
          className={`grid h-9 w-9 place-items-center rounded-tile ${admin ? 'hover:bg-white/10' : 'hover:bg-surface-2'}`}
        >
          <MenuIcon className="h-5 w-5" />
        </button>
        <Link to="/" className="flex items-center gap-sm">
          <BrandMark size={26} tone={tone} />
          <span className="text-nav-title font-extrabold tracking-tight">
            NamibiaGo <span className={admin ? 'text-white/60' : 'text-ink-2'}>{productName}</span>
          </span>
        </Link>
      </header>

      {/* Drawer backdrop (phone) */}
      {open ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden"
        />
      ) : null}

      {/* Sidebar — one element; a drawer below lg, a sticky column above. */}
      <aside
        id="portal-sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex w-[248px] flex-col transition-transform duration-200 ease-out lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:translate-x-0 ${
          open ? 'translate-x-0 shadow-card' : '-translate-x-full'
        } ${asideClass}`}
      >
        <div className="flex items-center justify-between px-lg pb-md pt-lg">
          <Link to="/" className="flex items-center gap-sm">
            <BrandMark size={34} tone={tone} />
            <span className="leading-tight">
              <span className="block text-nav-title font-extrabold tracking-tight">NamibiaGo</span>{' '}
              <span className={`block text-caption font-semibold ${admin ? 'text-white/55' : 'text-ink-2'}`}>
                {productName}
              </span>
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className={`grid h-8 w-8 place-items-center rounded-tile lg:hidden ${admin ? 'hover:bg-white/10' : 'hover:bg-surface-2'}`}
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        {sidebarTop ? <div className="px-md pb-md">{sidebarTop}</div> : null}

        <nav aria-label="Primary" className="flex-1 overflow-y-auto px-md">
          <ul className="flex flex-col gap-[2px]">
            {nav.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  activeOptions={{ exact: item.to === '/' }}
                  className={`group relative flex items-center gap-md rounded-tile px-md py-[9px] text-body font-semibold ${navBase}`}
                >
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full opacity-0 transition-opacity group-[.active]:opacity-100 ${
                      admin ? 'bg-canyon' : 'bg-canyon'
                    }`}
                  />
                  {item.icon ? (
                    <span className="grid h-5 w-5 place-items-center [&>svg]:h-5 [&>svg]:w-5">{item.icon}</span>
                  ) : null}
                  <span className="truncate">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="relative mt-md">
          {sidebarBottom ? <div className="relative z-10 px-md pb-[132px] pt-md">{sidebarBottom}</div> : <div className="h-[120px]" />}
          <Horizon tone={tone} />
        </div>
      </aside>

      {/* Page */}
      <div className="flex min-h-full min-w-0 flex-col">
        {banner}
        <main key={location.pathname} className="ng-reveal mx-auto w-full max-w-[1200px] flex-1 px-lg py-lg lg:px-xxl lg:py-xl">
          {children}
        </main>
      </div>
    </div>
  );
}

/** Sidebar bottom row: an icon button-ish link list (theme + sign-out). */
export function SidebarAction({
  onClick,
  icon,
  children,
  tone,
  subtle = false,
  testId,
}: {
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
  tone: 'business' | 'admin';
  subtle?: boolean;
  testId?: string;
}) {
  const admin = tone === 'admin';
  const cls = admin
    ? 'text-white/70 hover:bg-white/10 hover:text-white'
    : 'text-ink-2 hover:bg-surface-2 hover:text-ink';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex w-full items-center gap-md rounded-tile px-md py-[7px] text-left ${subtle ? 'text-small' : 'text-body font-semibold'} ${cls}`}
    >
      {icon ? <span className="grid h-5 w-5 place-items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">{icon}</span> : null}
      {children}
    </button>
  );
}
